import { LiveEventBufferBase } from "./LiveEventBuffer.shared"

/**
 * LiveEventBuffer - Web implementation
 *
 * Handles visibility changes and beforeunload events for web browsers.
 * Uses the Page Visibility API and beforeunload event.
 *
 * Usage:
 *   LiveEventBuffer.getInstance().init(liveSessionId, userId)
 *   LiveEventBuffer.getInstance().record('cam_toggle', { enabled: true })
 *   LiveEventBuffer.getInstance().destroy() // on session end
 */
let instance = null

class LiveEventBuffer extends LiveEventBufferBase {
    constructor() {
        super()
        this.beforeUnloadHandler = null
        this.visibilityChangeHandler = null
        this.previousVisibilityState = null
    }

    static getInstance() {
        if (!instance) {
            instance = new LiveEventBuffer()
        }
        return instance
    }

    /**
     * Setup handlers for tab close / visibility change (web only)
     */
    setupPlatformListeners() {
        if (typeof window === "undefined") return

        this.cleanupPlatformListeners()

        this.beforeUnloadHandler = () => this.flush()
        window.addEventListener("beforeunload", this.beforeUnloadHandler)

        // Track previous visibility state for out_of_app events
        this.previousVisibilityState = document.visibilityState

        this.visibilityChangeHandler = () => {
            const isHidden = document.visibilityState === "hidden"
            const wasHidden = this.previousVisibilityState === "hidden"

            // Record out_of_app events on visibility transitions (with 5s debounce)
            if (!wasHidden && isHidden) {
                this.recordOutOfAppStart()
            } else if (wasHidden && !isHidden) {
                this.recordOutOfAppEnd()
            }

            this.previousVisibilityState = document.visibilityState
        }
        window.addEventListener(
            "visibilitychange",
            this.visibilityChangeHandler
        )
    }

    cleanupPlatformListeners() {
        if (typeof window === "undefined") return

        if (this.beforeUnloadHandler) {
            window.removeEventListener("beforeunload", this.beforeUnloadHandler)
            this.beforeUnloadHandler = null
        }
        if (this.visibilityChangeHandler) {
            window.removeEventListener(
                "visibilitychange",
                this.visibilityChangeHandler
            )
            this.visibilityChangeHandler = null
        }
    }
}

export default LiveEventBuffer
